import * as THREE from 'three'
import gsap from 'gsap'
import GameState from '../Game/GameState.js'
import SaveSystem from '../Game/SaveSystem.js'
import { ARENA, TUNING } from '../Game/definitions.js'
import WaveUI from '../WaveUI.js'
import OssuaryUI from '../OssuaryUI.js'
import ModelFactory from './ModelFactory.js'
import Materials from './Materials.js'
import Controls from './Controls.js'
import Shadows from './Shadows.js'
import Sounds from './Sounds.js'
import DepositView from './DepositView.js'
import DepositIndicator from './DepositIndicator.js'
import BuildingView from './BuildingView.js'
import BuildingIndicator from './BuildingIndicator.js'
import OssuaryView from './OssuaryView.js'
import EngineerView from './EngineerView.js'
import UnitView from './UnitView.js'
import InventoryIndicator from './InventoryIndicator.js'
import InteractionTile, { createTextSprite } from './InteractionTile.js'

export default class World
{
    constructor(_options)
    {
        this.time = _options.time
        this.sizes = _options.sizes
        this.resources = _options.resources
        this.camera = _options.camera
        this.renderer = _options.renderer
        this.container = new THREE.Group()
        this.state = new GameState(SaveSystem.load())
        this.materials = new Materials({ resources: this.resources })
        this.factory = new ModelFactory({ resources: this.resources, materials: this.materials })
        this.sounds = new Sounds(this.resources)
        this.container.add(this.factory.createGround())
        this.shadows = new Shadows({ renderer: this.renderer })
        this.container.add(this.shadows.container)
        this.depositViews = new Map()
        this.depositIndicators = new Map()
        this.buildingViews = new Map()
        this.buildingIndicators = new Map()
        this.unitViews = new Map()
        this.enemyViews = new Map()
        this.flights = new Set()
        for(const deposit of this.state.deposits)
        {
            const view = new DepositView({ deposit, factory: this.factory })
            this.depositViews.set(deposit.id, view)
            this.container.add(view.container)
        }
        this.ossuaryView = new OssuaryView({ ossuary: this.state.ossuary, factory: this.factory })
        this.container.add(this.ossuaryView.container)
        this.engineer = new EngineerView({ factory: this.factory })
        this.engineer.container.position.set(this.state.player.x, this.state.player.y, 0)
        this.engineer.syncInventory(this.state.inventory)
        this.container.add(this.engineer.container)
        this.shadows.add(this.engineer.container)
        this.inventoryIndicator = new InventoryIndicator({ time: this.time, factory: this.factory })
        this.container.add(this.inventoryIndicator.container)
        this.controls = new Controls({ camera: this.camera, renderer: this.renderer })
        this.camera.player.set(this.state.player.x, this.state.player.y, 0)
        this.arena = this.factory.createArena()
        this.arena.visible = false
        this.obelisk = this.factory.createObelisk()
        this.obelisk.visible = false
        this.container.add(this.arena, this.obelisk)
        this.waveUI = new WaveUI({ state: this.state, onStart: () => this.startWave(), onDismiss: () => this.camera.stopWatching() })
        this.ossuaryUI = new OssuaryUI({ state: this.state })
        this.setEvents()
        for(const building of this.state.buildings)
            this.addBuilding(building, true)
        for(const unit of this.state.units)
        {
            if(unit.alive)
                this.addUnit(unit)
        }
        if(this.state.wave.active)
            this.onWaveStarted()
        this.showSite(this.state.activeSite)
        for(const id of ['bonePile', 'soulFont'])
        {
            const deposit = this.state.deposits.find((_item) => _item.id === id)
            const indicator = new DepositIndicator({ deposit, factory: this.factory, camera: this.camera })
            this.depositIndicators.set(id, indicator)
            this.container.add(indicator.container)
        }
        this.time.on('tick', () => this.update())
        this.autosave = new SaveSystem(this.state)
    }

    setEvents()
    {
        this.state.on('depositChanged', (_deposit) => this.depositViews.get(_deposit.id)?.sync())
        this.state.on('depositRegrown', (_deposit) => this.depositViews.get(_deposit.id)?.sync())
        this.state.on('depositRevealed', (_deposit) => this.revealDeposit(_deposit))
        this.state.on('gathered', ({ deposit, resource }) =>
        {
            this.sounds.play('woodHit', 0.16)
            this.inventoryIndicator.add(resource, this.state.inventory[resource])
            this.fly(resource, new THREE.Vector3(deposit.x, deposit.y, deposit.tree ? 1.8 : 0.8), () => this.playerTarget(), () => this.engineer.syncInventory(this.state.inventory))
            this.depositIndicators.get(deposit.id)?.dismiss()
        })
        this.state.on('siteTransfer', ({ site, resource, porter }) =>
        {
            if(!porter)
            {
                this.engineer.syncInventory(this.state.inventory)
                this.fly(resource, () => this.playerTarget(), new THREE.Vector3(site.x, site.y, 0.5))
            }
        })
        this.state.on('siteCompleted', ({ site, building }) =>
        {
            this.addBuilding(building)
            this.showSite(this.state.activeSite)
        })
        this.state.on('siteRevealed', (_site) =>
        {
            this.showSite(_site)
            this.camera.reveal(new THREE.Vector3(_site.x, _site.y, 0), this.engineer.container.position)
            this.sounds.play('reveal', 0.35)
        })
        this.state.on('buildingInput', ({ building, resource, porter }) =>
        {
            this.buildingViews.get(building.id)?.sync()
            this.buildingIndicators.get(building.id)?.dismiss()
            if(!porter)
            {
                this.engineer.syncInventory(this.state.inventory)
                this.fly(resource, () => this.playerTarget(), new THREE.Vector3(building.x, building.y - 0.8, 0.7))
            }
        })
        this.state.on('buildingChanged', (_building) => this.buildingViews.get(_building.id)?.sync())
        this.state.on('buildingProduced', (_building) => this.buildingViews.get(_building.id)?.sync())
        this.state.on('buildingOutput', ({ building, resource }) =>
        {
            this.engineer.syncInventory(this.state.inventory)
            this.inventoryIndicator.add(resource, this.state.inventory[resource])
            this.fly(resource, new THREE.Vector3(building.x, building.y - 1.2, 0.5), () => this.playerTarget(), () => this.engineer.syncInventory(this.state.inventory))
        })
        this.state.on('depositOutput', ({ deposit, resource }) =>
        {
            this.engineer.syncInventory(this.state.inventory)
            this.inventoryIndicator.add(resource, this.state.inventory[resource])
            this.fly(resource, new THREE.Vector3(deposit.x, deposit.y - 1.8, 0.5), () => this.playerTarget(), () => this.engineer.syncInventory(this.state.inventory))
        })
        this.state.on('ossuaryRevealed', () => this.ossuaryView.reveal())
        this.state.on('ossuaryChanged', () => this.ossuaryView.sync())
        this.state.on('ossuaryTransfer', () => this.engineer.syncInventory(this.state.inventory))
        this.state.on('unitRaised', ({ unit }) => this.addUnit(unit))
        this.state.on('unitDied', ({ unit }) => this.removeUnit(unit))
        this.state.on('unitWorked', () => this.sounds.play('woodHit', 0.06, 300))
        this.state.on('waveStarted', () => this.onWaveStarted())
        this.state.on('waveResolved', () => this.onWaveResolved())
        this.state.on('enemyDied', ({ enemy }) => this.removeEnemy(enemy))
        this.state.on('unitAttacked', ({ unit, target, ranged }) => this.combatEffect(unit, target, ranged ? '#72ffad' : '#d9f9df'))
        this.state.on('enemyAttacked', ({ enemy, target, ranged }) => this.combatEffect(enemy, target, ranged ? '#ff7186' : '#ffc0c5'))
    }

    addBuilding(_building, _restored = false)
    {
        const view = new BuildingView({ building: _building, factory: this.factory })
        this.buildingViews.set(_building.id, view)
        this.container.add(view.container)
        this.shadows.add(view.container)
        const indicator = new BuildingIndicator({ building: _building, factory: this.factory })
        this.buildingIndicators.set(_building.id, indicator)
        this.container.add(indicator.container)
        if(!_restored)
        {
            this.resolveBuildingSpawn(_building)
            this.sounds.play('area', 0.3)
        }
        if(_building.id === 'guardCrypt')
        {
            this.arena.visible = true
            this.obelisk.visible = true
            const label = createTextSprite('SOUL OBELISK  •  START WHEN READY', { background: 'rgba(20, 15, 31, 0.88)', color: '#c8ffdb' })
            label.position.set(ARENA.obelisk.x, ARENA.obelisk.y, 4.9)
            label.scale.set(4.1, 0.78, 1)
            this.container.add(label)
        }
    }

    showSite(_site)
    {
        if(this.tile)
        {
            this.container.remove(this.tile.container)
            this.tile.offscreenArrow.remove()
            this.tile = null
        }
        if(!_site || _site.built)
            return
        this.tile = new InteractionTile({ time: this.time, site: _site, factory: this.factory, camera: this.camera })
        this.container.add(this.tile.container)
    }

    revealDeposit(_deposit)
    {
        const view = this.depositViews.get(_deposit.id)
        view.reveal()
        const indicator = new DepositIndicator({ deposit: _deposit, factory: this.factory, camera: this.camera })
        this.depositIndicators.set(_deposit.id, indicator)
        this.container.add(indicator.container)
        this.camera.reveal(new THREE.Vector3(_deposit.x, _deposit.y, 0), this.engineer.container.position)
    }

    playerTarget()
    {
        return new THREE.Vector3(this.engineer.container.position.x, this.engineer.container.position.y, 1.55)
    }

    fly(_resource, _from, _to, _onComplete)
    {
        const item = this.factory.createItem(_resource, 1.2)
        this.container.add(item)
        const flight = { progress: 0 }
        this.flights.add(item)
        const from = typeof _from === 'function' ? _from() : _from
        item.position.copy(from)
        gsap.to(flight, {
            progress: 1,
            duration: 0.48,
            ease: 'power1.inOut',
            onUpdate: () =>
            {
                const to = typeof _to === 'function' ? _to() : _to
                item.position.lerpVectors(from, to, flight.progress)
                item.position.z += Math.sin(flight.progress * Math.PI) * 1.2
                item.rotation.z += 0.12
            },
            onComplete: () =>
            {
                this.container.remove(item)
                this.flights.delete(item)
                _onComplete?.()
            }
        })
    }

    addUnit(_unit)
    {
        const view = new UnitView({ unit: _unit, factory: this.factory })
        this.unitViews.set(_unit.id, view)
        view.container.scale.setScalar(0.01)
        gsap.to(view.container.scale, { x: 1, y: 1, z: 1, duration: 0.45, ease: 'back.out(2)' })
        this.container.add(view.container)
    }

    removeUnit(_unit)
    {
        const view = this.unitViews.get(_unit.id)
        if(!view)
            return
        this.unitViews.delete(_unit.id)
        gsap.to(view.container.scale, { x: 0.01, y: 0.01, z: 0.01, duration: 0.4, onComplete: () => this.disposeView(view.container) })
    }

    onWaveStarted()
    {
        for(const enemy of this.state.wave.enemies)
        {
            if(!enemy.alive)
                continue
            const view = new UnitView({ unit: enemy, factory: this.factory, enemy: true })
            this.enemyViews.set(enemy.id, view)
            this.container.add(view.container)
        }
        this.camera.watch(new THREE.Vector3(ARENA.center.x, ARENA.center.y, 0))
    }

    onWaveResolved()
    {
        for(const view of this.enemyViews.values())
            this.disposeView(view.container)
        this.enemyViews.clear()
    }

    removeEnemy(_enemy)
    {
        const view = this.enemyViews.get(_enemy.id)
        if(!view)
            return
        this.enemyViews.delete(_enemy.id)
        gsap.to(view.container.scale, { x: 0.01, y: 0.01, z: 0.01, duration: 0.35, onComplete: () => this.disposeView(view.container) })
    }

    disposeView(_container)
    {
        this.container.remove(_container)
        _container.traverse((_child) =>
        {
            if(_child.isMesh)
                _child.geometry.dispose()
        })
    }

    combatEffect(_from, _to, _color)
    {
        const material = new THREE.MeshBasicMaterial({ color: _color, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false })
        const orb = new THREE.Mesh(new THREE.SphereGeometry(0.11, 7, 5), material)
        orb.position.set(_from.x, _from.y, 1.2)
        this.container.add(orb)
        gsap.to(orb.position, { x: _to.x, y: _to.y, z: 0.9, duration: 0.2, onComplete: () => { this.container.remove(orb); orb.geometry.dispose(); material.dispose() } })
    }

    startWave()
    {
        if(this.state.startNextWave())
            this.ossuaryUI.setOpen(false)
    }

    resolveBuildingSpawn(_building)
    {
        const position = this.engineer.container.position
        const delta = new THREE.Vector2(position.x - _building.x, position.y - _building.y)
        const length = delta.length()
        if(length >= 1.75)
            return
        if(length < 0.01)
            delta.set(0, -1)
        else
            delta.normalize()
        position.set(_building.x + delta.x * 1.8, _building.y + delta.y * 1.8, 0)
        this.state.setPlayerPosition(position.x, position.y)
    }

    isPositionValid(_x, _y)
    {
        if(Math.hypot(_x, _y) > TUNING.walkRadius)
            return false
        if(this.state.ossuary.revealed && Math.hypot(_x - this.state.ossuary.x, _y - this.state.ossuary.y) < 1.45)
            return false
        for(const deposit of this.state.deposits)
        {
            if(!deposit.revealed || deposit.remaining <= 0)
                continue
            const radius = deposit.tree ? 0.55 : 2.3
            if(Math.hypot(_x - deposit.x, _y - deposit.y) < radius + 0.36)
                return false
        }
        for(const building of this.state.buildings)
        {
            if(Math.hypot(_x - building.x, _y - building.y) < 1.45)
                return false
        }
        if(this.obelisk.visible && Math.hypot(_x - ARENA.obelisk.x, _y - ARENA.obelisk.y) < 1.35)
            return false
        return true
    }

    movePlayer(_input, _delta)
    {
        const position = this.engineer.container.position
        if(_input.lengthSq() === 0)
            return false
        const forward = new THREE.Vector2(-1, 1.45).normalize()
        const right = new THREE.Vector2(1.45, 1).normalize()
        const movement = new THREE.Vector2().addScaledVector(right, _input.x).addScaledVector(forward, _input.y).clampLength(0, 1).multiplyScalar(TUNING.playerSpeed * _delta)
        const oldX = position.x
        const oldY = position.y
        const nextX = oldX + movement.x
        const nextY = oldY + movement.y
        if(this.isPositionValid(nextX, nextY))
            position.set(nextX, nextY, 0)
        else
        {
            if(this.isPositionValid(nextX, oldY))
                position.x = nextX
            if(this.isPositionValid(position.x, nextY))
                position.y = nextY
        }
        const moved = Math.hypot(position.x - oldX, position.y - oldY) > 0.0001
        if(moved)
            this.engineer.container.rotation.z = Math.atan2(- movement.x, movement.y)
        return moved
    }

    update()
    {
        const delta = Math.min(this.time.delta / 1000, 0.06)
        const input = this.controls.update()
        if(this.ossuaryUI.isOpen || this.state.wave.result)
            input.set(0, 0)
        if(input.lengthSq() > 0 && this.state.wave.active)
            this.camera.stopWatching()
        const moving = this.movePlayer(input, delta)
        const position = this.engineer.container.position
        this.state.setPlayerPosition(position.x, position.y)
        this.state.update(delta, moving)
        this.camera.player.set(position.x, position.y, 0)
        if(this.state.action && !moving)
        {
            const deposit = this.state.action.deposit
            this.engineer.container.rotation.z = Math.atan2(- (deposit.x - position.x), deposit.y - position.y)
        }
        this.engineer.update(delta, moving, this.state.action)
        this.inventoryIndicator.update(position)
        if(this.tile)
        {
            const site = this.tile.site
            const inside = Math.abs(position.x - site.x) <= TUNING.tileHalfSize && Math.abs(position.y - site.y) <= TUNING.tileHalfSize
            this.tile.update(inside && !moving, this.state.shouldGuideToSite(site))
        }
        for(const view of this.buildingViews.values())
            view.update(this.time.elapsed)
        for(const view of this.unitViews.values())
            view.update(delta, this.time.elapsed)
        for(const view of this.enemyViews.values())
        {
            if(view.unit.alive)
                view.update(delta, this.time.elapsed)
        }
        for(const indicator of this.depositIndicators.values())
            indicator.update(this.time.elapsed)
        for(const indicator of this.buildingIndicators.values())
            indicator.update(this.time.elapsed)
        this.arena.userData.portal.rotation.z = this.time.elapsed * 0.0007
        const healthRatio = this.state.wave.obeliskHealth / TUNING.obeliskHealth
        this.obelisk.userData.glow.scale.setScalar(0.85 + healthRatio * 0.15 + Math.sin(this.time.elapsed * 0.006) * 0.05)
        this.ossuaryView.sync()
        this.waveUI.update()
        this.ossuaryUI.update()
    }
}
